/**
 * The form-schema adapter — frozen interface `I3` as the editors consume it.
 *
 * **The UI codes against the schema, not against the Go types.** Every closed
 * enum, every requiredness rule, every piece of help text and — most importantly
 * — every field's *status* comes from `GET /v1/policy-schema` at runtime. Nothing
 * in `pages/policy/` hard-codes a vocabulary, because a drift check
 * (`SC-F501`) defends the schema and nothing defends a hard-coded list: the
 * moment the pinned Sluice moves, a literal here re-offers a field the build no
 * longer reads and the product is lying again.
 *
 * This module is the ONE place that narrows the schema document, which is why
 * `api/policies.ts` leaves `groups`, `enums` and `fields` deliberately open. The
 * narrowing is defensive throughout: an unknown group answers no fields, an
 * unknown enum answers no options, and an unknown status answers "offer it" —
 * because a schema this bundle does not fully understand must degrade to a usable
 * form rather than to a blank one.
 *
 * Implements the schema-driven half of `SC-F501`, `SC-F502` and `SC-F505`.
 */

import type { BnSelectOption } from "../../components/index.js";
import {
  getPolicyFormSchema,
  type CelEnvironmentDoc,
  type CelEnvironmentName,
  type FormFieldOffer,
  type FormFieldStatus,
  type PolicyFormSchema,
  type PolicyFormSchemaResponse,
  type PolicyKind,
  type PolicyKindSchema,
} from "../../api/policies.js";

/** SchemaItems describes an array field's element type. */
export interface SchemaItems {
  type: string;
  /** The group carrying the element's own fields, when the element is an object. */
  group?: string;
  /** `wildcard`, `cidr`, `tag`, `scalar`, … */
  format?: string;
  pattern?: string;
  enum?: string;
}

/**
 * SchemaField is one field entry. Every property is optional except the three
 * the schema always carries, because the document is hand-maintained and a
 * missing key must not throw in a form.
 */
export interface SchemaField {
  name: string;
  /** `string`, `integer`, `boolean`, `object`, `array`, `map`, `scalar`. */
  type: string;
  /** The widget hint: `text`, `number`, `select`, `segmented`, `radio`,
   * `hidden`, `disabled`, `selector`, `predicate-tree`, `typed-value`, … */
  widget?: string;
  /** The group carrying this field's own fields, when `type` is `object`. */
  group?: string;
  items?: SchemaItems;
  /** The name of the closed enum this field draws from. */
  enum?: string;
  required?: boolean;
  /** `sluice` when the engine refuses the object without it, `us` when Sluice
   * accepts the omission and the product refuses it anyway. The distinction is
   * shown, because "required by us" is a product decision an author may
   * reasonably want explained. */
  requiredBy?: "sluice" | "us";
  /** Conditional requiredness: required when the named sibling holds one of
   * these values. `["*"]` means "whenever that sibling is set at all". */
  requiredWhen?: { field: string; in: string[] };
  pattern?: string;
  format?: string;
  maxLength?: number;
  minItems?: number;
  min?: number;
  max?: number;
  /** What Sluice assumes when the field is absent. */
  default?: unknown;
  /** What a NEWLY CREATED object starts at, where that differs from Sluice's
   * default — `enforcementMode` defaults to `Enforce` upstream and starts at
   * `Audit` here (`SC-S502`, `SC-F527`). */
  initial?: unknown;
  status?: FormFieldStatus;
  /** Overrides the status vocabulary's own badge text. */
  badge?: string;
  /** Why the field is not offered, in runtime terms. */
  reason?: string;
  /** The `SLUICE-U##` ask that would make the field work. */
  upstream?: string;
  /** The Sluice source that proves it, at the pinned commit. */
  source?: string;
  help?: string;
  /** False when Sluice accepts any value here — the archetype for a closed
   * dropdown (`SC-F506`). */
  validatedUpstream?: boolean;
}

/** SchemaGroup is a reusable field set. `include` pulls in another group's
 * fields first, so `rowFilterSpec` is `policyCommon` plus two fields. */
export interface SchemaGroup {
  help?: string;
  include?: string[];
  fields?: SchemaField[];
}

/** SchemaEnumValue is one member of a closed enum. */
export interface SchemaEnumValue {
  value: string;
  label: string;
  help?: string;
  default?: boolean;
  status?: FormFieldStatus;
  reason?: string;
  source?: string;
}

/** SchemaEnum is one closed vocabulary. */
export interface SchemaEnum {
  origin?: string;
  goType?: string;
  source?: string;
  /** False means Sluice takes any string here: an unknown value compiles and
   * then silently never matches, or becomes an HTTP 500. */
  validatedUpstream?: boolean;
  help?: string;
  values?: SchemaEnumValue[];
}

/** StatusMeta is one row of the schema's status vocabulary. */
export interface StatusMeta {
  status: FormFieldStatus | "";
  badge: string;
  help: string;
  offer: FormFieldOffer;
}

/** The status a field with no explicit status carries. */
const ENFORCED: StatusMeta = { status: "enforced", badge: "", help: "", offer: "yes" };

/**
 * PolicySchema wraps the schema document with the lookups an editor needs.
 *
 * It holds the document by reference and computes nothing eagerly: the schema is
 * static for the life of the deployment, so a memoized derivation would only add
 * a cache to invalidate.
 */
export class PolicySchema {
  constructor(readonly doc: PolicyFormSchema) {}

  /** kind returns one kind's form definition. */
  kind(kind: PolicyKind): PolicyKindSchema | undefined {
    return (this.doc.kinds ?? []).find((k) => k.kind === kind);
  }

  /**
   * fields returns a group's fields with `include` resolved, included fields
   * first and an own field of the same name winning.
   *
   * The override matters: `objectMetaInert` includes `objectMeta` and then
   * redeclares `priority` as hidden, because priority orders nothing on the four
   * non-enforcement kinds. Concatenating without overriding would offer a number
   * that decides nothing.
   */
  fields(groupName: string, seen: Set<string> = new Set()): SchemaField[] {
    if (seen.has(groupName)) return [];
    seen.add(groupName);
    const group = this.group(groupName);
    if (!group) return [];
    const out: SchemaField[] = [];
    for (const parent of group.include ?? []) out.push(...this.fields(parent, seen));
    for (const field of group.fields ?? []) {
      const at = out.findIndex((f) => f.name === field.name);
      if (at >= 0) out[at] = field;
      else out.push(field);
    }
    return out;
  }

  /** group returns one raw group entry. */
  group(name: string): SchemaGroup | undefined {
    const groups = (this.doc.groups ?? {}) as Record<string, SchemaGroup | undefined>;
    return groups[name];
  }

  /** groupHelp is the sentence the schema attaches to a group — the source of
   * the selector's OR explainer (`SC-F504`) and the predicate node's
   * one-shape-per-node statement. */
  groupHelp(name: string): string {
    return this.group(name)?.help ?? "";
  }

  /** field looks one field up inside a group, following `include`. */
  field(groupName: string, fieldName: string): SchemaField | undefined {
    return this.fields(groupName).find((f) => f.name === fieldName);
  }

  /** help is a field's help text, or the empty string. Every editor renders this
   * rather than its own prose, so the sentence a reviewer approved in the schema
   * is the sentence the author reads. */
  help(groupName: string, fieldName: string): string {
    return this.field(groupName, fieldName)?.help ?? "";
  }

  /** enumOf returns one closed vocabulary. */
  enumOf(name: string | undefined): SchemaEnum | undefined {
    if (!name) return undefined;
    const enums = (this.doc.enums ?? {}) as Record<string, SchemaEnum | undefined>;
    return enums[name];
  }

  /** enumHelp is the vocabulary's own explanation — for `ClaimOp` it is the
   * sentence that says Sluice does not validate the field at all. */
  enumHelp(name: string | undefined): string {
    return this.enumOf(name)?.help ?? "";
  }

  /**
   * options renders an enum as bn-select options.
   *
   * A member the pinned build refuses or ignores is **disabled with its reason
   * visible**, never dropped: an author who imported it must see why it cannot be
   * picked, and a vocabulary that silently loses members reads as a shorter enum
   * rather than as a constrained one. A `hidden` member is the one exception —
   * offering it would promise behaviour that never happens.
   */
  options(name: string | undefined, opts: { includeHidden?: boolean } = {}): BnSelectOption[] {
    const values = this.enumOf(name)?.values ?? [];
    const out: BnSelectOption[] = [];
    for (const v of values) {
      const meta = this.status(v.status);
      if (meta.offer === "hidden" && !opts.includeHidden) continue;
      const option: BnSelectOption = { value: v.value, label: v.label || v.value };
      if (v.help) option.description = v.help;
      if (meta.offer === "no") {
        option.disabled = true;
        option.disabledReason = reasonText(meta, v.reason);
      } else if (meta.offer === "badged" && v.reason) {
        // Selectable, and the reader is told the value changes no outcome.
        option.description = `${meta.badge}: ${v.reason}${v.help ? ` — ${v.help}` : ""}`;
      }
      out.push(option);
    }
    return out;
  }

  /** status resolves a status name against the schema's own vocabulary. An
   * unknown status — a newer schema than this bundle — is treated as enforced, so
   * the form stays usable rather than disabling everything it cannot classify. */
  status(status: FormFieldStatus | undefined): StatusMeta {
    if (!status) return ENFORCED;
    const row = (this.doc.statuses ?? []).find((s) => s.status === status);
    if (!row) return ENFORCED;
    return { status: row.status, badge: row.badge, help: row.help, offer: row.offer };
  }

  /** offer is what the editor may do with a field. */
  offer(field: SchemaField | undefined): FormFieldOffer {
    if (!field) return "yes";
    // A `hidden` widget is the schema saying "never render this" independently of
    // status — `metadata.namespace` is hidden and declared-only, and the widget is
    // the instruction while the status is the reason.
    if (field.widget === "hidden") return "hidden";
    if (field.widget === "disabled") return "no";
    return this.status(field.status).offer;
  }

  /** badge is the honest-label chip text for a field: the field's own override,
   * else its status vocabulary's. */
  badge(field: SchemaField | undefined): string {
    if (!field) return "";
    return field.badge || this.status(field.status).badge;
  }

  /** disabledReason composes the sentence shown beside a disabled field: the
   * runtime consequence first, then the status vocabulary's general statement,
   * then the upstream ask that would fix it. */
  disabledReason(field: SchemaField | undefined): string {
    if (!field) return "";
    const meta = this.status(field.status);
    const parts = [field.reason, meta.help, field.upstream ? `Tracked as ${field.upstream}.` : ""];
    return parts.filter(Boolean).join(" ");
  }

  /**
   * required answers whether a field must be present, resolving `requiredWhen`
   * against its siblings.
   *
   * `in: ["*"]` means "whenever the named sibling is set at all", which is how the
   * schema expresses `issuer` being required once `jwksUrl` is.
   */
  required(field: SchemaField | undefined, siblings: Record<string, unknown> = {}): boolean {
    if (!field) return false;
    if (field.required) return true;
    const when = field.requiredWhen;
    if (!when) return false;
    const raw = siblings[when.field.split(".").pop() ?? when.field];
    if (raw === undefined || raw === null || raw === "") return false;
    if (when.in.includes("*")) return true;
    return when.in.includes(String(raw));
  }

  /** enumDefault is the value the enum marks as Sluice's own default. */
  enumDefault(name: string | undefined): string {
    const values = this.enumOf(name)?.values ?? [];
    return values.find((v) => v.default)?.value ?? "";
  }

  /** initial is what a newly created object starts a field at: the schema's
   * `initial` when it names one, else Sluice's own `default`. */
  initial(field: SchemaField | undefined): unknown {
    if (!field) return undefined;
    return field.initial !== undefined ? field.initial : field.default;
  }

  /**
   * celEnvironment returns one CEL environment's declared vocabulary
   * (`SC-F510`).
   *
   * The editor's completion list comes from here and never from a literal in this
   * bundle, and the reason is sharper than the usual one: the control-plane linter
   * REFUSES an expression naming a variable or a field outside this list, so a
   * hard-coded list here would offer completions the save then rejects. It answers
   * undefined for an unknown environment rather than an empty document, so a
   * caller can degrade to a plain editor instead of to an empty picker.
   */
  celEnvironment(name: CelEnvironmentName): CelEnvironmentDoc | undefined {
    return (this.doc.celVocabulary ?? []).find((e) => e.environment === name);
  }
}

/** reasonText composes a disabled enum member's reason. */
function reasonText(meta: StatusMeta, reason?: string): string {
  return [meta.badge ? `${meta.badge}.` : "", reason, meta.help].filter(Boolean).join(" ");
}

/** LoadedSchema is the schema plus the provenance the response wraps it in. */
export interface LoadedSchema {
  schema: PolicySchema;
  response: PolicyFormSchemaResponse;
}

let pending: Promise<LoadedSchema> | null = null;

/**
 * loadPolicySchema fetches and caches the form schema for the session.
 *
 * One module-level promise, shared by every editor on the page: the document is
 * immutable for the life of the deployment (it is versioned with the pinned
 * Sluice) and it is large, so a policy list rendering twelve rows and an editor
 * opening beside it must not fetch it thirteen times. A rejected load is not
 * cached, so a transient failure is retried by the next caller rather than
 * poisoning the session.
 */
export function loadPolicySchema(): Promise<LoadedSchema> {
  pending ??= getPolicyFormSchema()
    .then((response) => ({ response, schema: new PolicySchema(response.schema) }))
    .catch((err: unknown) => {
      pending = null;
      throw err;
    });
  return pending;
}
